import React from "react";
import AotdArtCard from "../AotdArtCard";
import AotdQuoteForm from "../AotdQuoteForm";
import { QuoteWindowCountdown, useQuoteWindow } from "../AotdQuoteWindow";
import NextAOTD from "../../sections/nextAotd/NextAotd";
import type { ArtInfo } from "../../api/types";

/**
 * Final "completed today" screen: art card + (when author) quote form +
 * next-AOTD emoji selector. Mirrors monolith lines 1595–1736.
 */
export interface CompletedStateProps {
  art: ArtInfo;
  artUrl: string | null;
  hasPermission: boolean;
  isAuthenticated: boolean;
  currentUsername: string | null;
  streakIcon: URL | null;
  generationElapsedTime?: string;
  onTriggerWayback: () => void | Promise<void>;
  quote: {
    input: string;
    set: (next: string) => void;
    submit: () => void | Promise<void>;
    suggest: () => void | Promise<void>;
    helpUsedCount: number;
    suggestionLoading: boolean;
  };
  emojis: {
    next: string[];
  };
}

export const CompletedState: React.FC<CompletedStateProps> = ({
  art,
  artUrl,
  hasPermission,
  isAuthenticated,
  currentUsername,
  streakIcon,
  generationElapsedTime,
  onTriggerWayback,
  quote,
  emojis,
}) => {
  // The quote goes on prints, so it can only be set in the first 45 minutes after the art
  // appears (aotdsys-1.2); once that passes the form just goes away.
  const quoteWindow = useQuoteWindow(art.quoteWindowEndsAt);
  const showQuoteForm = hasPermission && !art.authorQuote && quoteWindow.open;
  // The finished card is the only place an author can stage next-AOTD
  // emojis. If this one came out without any, say what skipping it costs.
  // While AOTD guessing runs the list is empty for the author too, so an
  // empty list then means "hidden", not "none".
  const emojilessOwnArt =
    hasPermission &&
    !art.emojisHidden &&
    (art.thisAOTDEmojis?.length ?? 0) === 0 &&
    emojis.next.length === 0;

  return (
    <div className="art-of-the-day">
      <AotdArtCard
        artInfo={art}
        artUrl={artUrl}
        streakIcon={streakIcon}
        generationElapsedTime={generationElapsedTime}
        hasPermission={hasPermission}
        isAuthenticated={isAuthenticated}
        currentUsername={currentUsername}
        onTriggerWayback={onTriggerWayback}
      />
      {showQuoteForm && <QuoteWindowCountdown msLeft={quoteWindow.msLeft} />}
      {showQuoteForm && (
        <AotdQuoteForm
          value={quote.input}
          onChange={quote.set}
          onSubmit={quote.submit}
          onSuggest={quote.suggest}
          helpUsedCount={quote.helpUsedCount}
          suggestionLoading={quote.suggestionLoading}
        />
      )}
      {emojilessOwnArt ? (
        <p
          role="note"
          style={{
            maxWidth: "420px",
            margin: "14px auto 0",
            padding: "10px 14px",
            borderRadius: "10px",
            background: "#fffbeb",
            border: "1px solid #fcd34d",
            color: "#92400e",
            fontSize: "0.8rem",
            lineHeight: 1.45,
            textAlign: "left",
          }}
        >
          This AOTD has no emojis. Pick some for your next one below: from now on, racing
          without them adds +450ms to your time (it hits once, and racing the next day too
          waives it).
        </p>
      ) : null}
      {hasPermission && <NextAOTD nextEmojis={emojis.next} />}
    </div>
  );
};

export default CompletedState;
